/** The kinds of Job field a filter condition can target. Drives which operators
 *  are offered and how a value is compared. */
export type FilterFieldType = "text" | "number" | "boolean" | "date" | "select" | "multiSelect";

/** Every operator across all types. Semantics are type-dispatched in `matchOperator`
 *  (e.g. `is` is exact-string for text but same-day for date). */
export type OperatorId =
	| "is"
	| "isNot"
	| "contains"
	| "doesNotContain"
	| "isAnyOf"
	| "isNoneOf"
	| "hasAnyOf"
	| "hasAllOf"
	| "hasNoneOf"
	| "eq"
	| "neq"
	| "lt"
	| "lte"
	| "gt"
	| "gte"
	| "isBefore"
	| "isAfter"
	| "isOnOrBefore"
	| "isOnOrAfter"
	| "isEmpty"
	| "isNotEmpty";

/** A job's extracted value for a field (what `FilterField.getValue` yields). */
export type FilterFieldValue = string | number | boolean | string[] | null | undefined;
/** The user-supplied operand stored on a condition. */
export type FilterOperand = string | number | boolean | string[] | null;

export interface OperatorMeta {
	id: OperatorId;
	label: string;
	/** false for isEmpty/isNotEmpty — those take no operand and render no value input. */
	needsValue: boolean;
}

const EMPTY_OPS: readonly OperatorMeta[] = [
	{ id: "isEmpty", label: "is empty", needsValue: false },
	{ id: "isNotEmpty", label: "is not empty", needsValue: false },
];

export const OPERATORS_BY_TYPE: Record<FilterFieldType, readonly OperatorMeta[]> = {
	text: [
		{ id: "is", label: "is", needsValue: true },
		{ id: "isNot", label: "is not", needsValue: true },
		{ id: "contains", label: "contains", needsValue: true },
		{ id: "doesNotContain", label: "does not contain", needsValue: true },
		...EMPTY_OPS,
	],
	select: [
		{ id: "is", label: "is", needsValue: true },
		{ id: "isNot", label: "is not", needsValue: true },
		{ id: "isAnyOf", label: "is any of", needsValue: true },
		{ id: "isNoneOf", label: "is none of", needsValue: true },
		...EMPTY_OPS,
	],
	multiSelect: [
		{ id: "hasAnyOf", label: "has any of", needsValue: true },
		{ id: "hasAllOf", label: "has all of", needsValue: true },
		{ id: "hasNoneOf", label: "has none of", needsValue: true },
		...EMPTY_OPS,
	],
	number: [
		{ id: "eq", label: "=", needsValue: true },
		{ id: "neq", label: "≠", needsValue: true },
		{ id: "lt", label: "<", needsValue: true },
		{ id: "lte", label: "≤", needsValue: true },
		{ id: "gt", label: ">", needsValue: true },
		{ id: "gte", label: "≥", needsValue: true },
		...EMPTY_OPS,
	],
	date: [
		{ id: "is", label: "is", needsValue: true },
		{ id: "isBefore", label: "is before", needsValue: true },
		{ id: "isAfter", label: "is after", needsValue: true },
		{ id: "isOnOrBefore", label: "is on or before", needsValue: true },
		{ id: "isOnOrAfter", label: "is on or after", needsValue: true },
		...EMPTY_OPS,
	],
	boolean: [{ id: "is", label: "is", needsValue: true }],
};

/** The operator a fresh condition starts with — first listed for the type. */
export function defaultOperator(type: FilterFieldType): OperatorId {
	return OPERATORS_BY_TYPE[type][0].id;
}

/** A job value counts as empty when it carries no information: null/undefined,
 *  the empty string, or the empty array. Numeric 0 and boolean false are NOT empty. */
function isEmptyValue(v: FilterFieldValue): boolean {
	if (v === null || v === undefined) return true;
	if (typeof v === "string") return v.trim() === "";
	if (Array.isArray(v)) return v.length === 0;
	return false;
}

/** Operators that should match empty job values ("everything except X"). */
const NEGATIVE_OPS = new Set<OperatorId>(["isNot", "doesNotContain", "isNoneOf", "hasNoneOf", "neq"]);

function asString(v: FilterFieldValue | FilterOperand): string {
	return typeof v === "string" ? v : v == null ? "" : String(v);
}
function asStringArray(v: FilterFieldValue | FilterOperand): string[] {
	return Array.isArray(v) ? v.map(String) : [];
}

/** Evaluate one condition against one job's field value. `type` selects the
 *  comparison family; empty job values follow the negative/positive rule above. */
export function matchOperator(
	type: FilterFieldType,
	operator: OperatorId,
	jobValue: FilterFieldValue,
	operand: FilterOperand,
): boolean {
	if (operator === "isEmpty") return isEmptyValue(jobValue);
	if (operator === "isNotEmpty") return !isEmptyValue(jobValue);
	if (isEmptyValue(jobValue)) return NEGATIVE_OPS.has(operator);

	switch (type) {
		case "text": {
			const hay = asString(jobValue).toLowerCase();
			const needle = asString(operand).toLowerCase();
			if (operator === "is") return hay === needle;
			if (operator === "isNot") return hay !== needle;
			if (operator === "contains") return hay.includes(needle);
			if (operator === "doesNotContain") return !hay.includes(needle);
			return false;
		}
		case "select": {
			const val = asString(jobValue).toLowerCase();
			if (operator === "is") return val === asString(operand).toLowerCase();
			if (operator === "isNot") return val !== asString(operand).toLowerCase();
			const set = new Set(asStringArray(operand).map((s) => s.toLowerCase()));
			if (operator === "isAnyOf") return set.has(val);
			if (operator === "isNoneOf") return !set.has(val);
			return false;
		}
		case "multiSelect": {
			const vals = new Set(asStringArray(jobValue).map((s) => s.toLowerCase()));
			const operands = asStringArray(operand).map((s) => s.toLowerCase());
			if (operator === "hasAnyOf") return operands.some((o) => vals.has(o));
			if (operator === "hasAllOf") return operands.every((o) => vals.has(o));
			if (operator === "hasNoneOf") return !operands.some((o) => vals.has(o));
			return false;
		}
		case "number": {
			const a = typeof jobValue === "number" ? jobValue : Number(jobValue);
			const b = typeof operand === "number" ? operand : Number(operand);
			if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
			if (operator === "eq") return a === b;
			if (operator === "neq") return a !== b;
			if (operator === "lt") return a < b;
			if (operator === "lte") return a <= b;
			if (operator === "gt") return a > b;
			if (operator === "gte") return a >= b;
			return false;
		}
		case "date": {
			const a = asString(jobValue);
			const b = asString(operand);
			if (!a || !b) return false;
			if (operator === "is") return a === b;
			if (operator === "isBefore") return a < b;
			if (operator === "isAfter") return a > b;
			if (operator === "isOnOrBefore") return a <= b;
			if (operator === "isOnOrAfter") return a >= b;
			return false;
		}
		case "boolean":
			return Boolean(jobValue) === Boolean(operand);
	}
}
